import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Container, Row, Col, Modal, Form, Button, Alert } from 'react-bootstrap';
import { galleryApi } from '../services/api';
import PageMeta from '../components/PageMeta';
import '../styles/pages.css';
import '../styles/dia-de-campo.css';

const DDC_GALLERY_ID = 8;

/* ── Contadores ─────────────────────────────────────── */
const stats = [
  { value: 3200, suffix: '+', label: 'Pessoas impactadas' },
  { value: 80,   suffix: '+', label: 'Empresas participantes' },
  { value: 8,    suffix: 'ª', label: 'Edições realizadas' },
];

function useCountUp(target: number, duration = 1800, active: boolean) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!active) return;
    let start = 0;
    const step = Math.ceil(target / (duration / 16));
    const timer = setInterval(() => {
      start += step;
      if (start >= target) { setCount(target); clearInterval(timer); }
      else setCount(start);
    }, 16);
    return () => clearInterval(timer);
  }, [active, target, duration]);
  return count;
}

const StatItem: React.FC<{ value: number; suffix: string; label: string; active: boolean }> = ({ value, suffix, label, active }) => {
  const count = useCountUp(value, 1600, active);
  return (
    <div className="ddc-stat">
      <span className="ddc-stat-number">{count.toLocaleString('pt-BR')}{suffix}</span>
      <span className="ddc-stat-label">{label}</span>
    </div>
  );
};

/* ── Dados de patrocínio ─────────────────────────────── */
const patrocinios = [
  {
    key: 'master',
    nome: 'Patrocínio Master',
    preco: 'R$ 30.000',
    destaque: true,
    beneficios: [
      'Co-realizador do evento',
      'Logo em todo o material de divulgação',
      'Logo no painel de Led das palestras',
      'Logo nas canecas personalizadas',
      'Banner na entrada da propriedade',
      'Banner no espaço do evento',
      'Citação durante o evento e entrevistas',
      'Espaço na feira 10×10m',
      'Logo nos crachás dos participantes',
      'Espaço de 15 min no palco principal',
      'Assinatura no vídeo exclusivo',
      'Kit exclusivo da empresa para distribuição',
    ],
  },
  {
    key: 'ouro',
    nome: 'Patrocínio Ouro',
    preco: 'R$ 10.000',
    destaque: false,
    beneficios: [
      'Logo nas mídias contratadas',
      'Logo nos materiais impressos',
      'Banner na entrada do evento',
      'Citações nas entrevistas',
      'Logo em looping no telão do evento',
      'Logo em Backdrop do evento',
      'Espaço na feira até 8×4m',
    ],
  },
  {
    key: 'prata',
    nome: 'Patrocínio Prata',
    preco: 'R$ 5.000',
    destaque: false,
    beneficios: [
      'Logo no material de divulgação',
      'Logo em Backdrop do evento',
      'Banner na entrada do evento',
      'Espaço na feira até 4×4m',
      'Logo na transmissão ao vivo',
      'Divulgação nas mídias do evento',
      'Citações nas entrevistas',
    ],
  },
];

const expositores = [
  {
    key: 'campo',
    nome: 'Expositor Campo',
    preco: 'R$ 10.000',
    beneficios: [
      'Plot de testagem (20 linhas × 50m)',
      'Espaço na feira 4×4m',
      'Divulgação nas mídias',
      'Divulgação no material impresso',
      'Citação em entrevistas',
    ],
  },
  {
    key: 'feira-4x4',
    nome: 'Expositor Feira 4×4m',
    preco: 'R$ 2.000',
    beneficios: [
      'Espaço 4×4m no evento',
      'Divulgação nas mídias',
      'Citação em entrevistas',
    ],
  },
  {
    key: 'feira-10x10',
    nome: 'Expositor Feira 10×10m',
    preco: 'R$ 3.000',
    beneficios: [
      'Espaço 10×10m no evento',
      'Divulgação nas mídias',
      'Citação em entrevistas',
    ],
  },
];

/* ── Componente principal ────────────────────────────── */
const DiaDeCampo: React.FC = () => {
  const [current, setCurrent] = useState(0);
  const [slides, setSlides] = useState<{ src: string; alt: string }[]>([]);
  const [statsActive, setStatsActive] = useState(false);
  const statsRef = useRef<HTMLDivElement>(null);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    galleryApi.getById(DDC_GALLERY_ID).then(galeria => {
      const fotos = [...galeria.fotos].sort((a, b) => a.ordem - b.ordem);
      setSlides(fotos.map(f => ({ src: f.url, alt: f.legenda || 'Dia de Campo Agricampanha' })));
    }).catch(() => {});
  }, []);
  const [showModal, setShowModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState('');
  const [showSuccess, setShowSuccess] = useState(false);
  const [formData, setFormData] = useState({ nome: '', email: '', telefone: '', empresa: '', mensagem: '' });

  const startTimer = useCallback((len: number) => {
    if (timerRef.current) clearInterval(timerRef.current);
    if (len <= 1) return;
    timerRef.current = setInterval(() => setCurrent(p => (p + 1) % len), 3500);
  }, []);

  useEffect(() => {
    startTimer(slides.length);
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [slides.length, startTimer]);

  const goTo = useCallback((index: number, len: number) => {
    setCurrent(index);
    startTimer(len);
  }, [startTimer]);

  // Stats IntersectionObserver
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setStatsActive(true); },
      { threshold: 0.3 }
    );
    if (statsRef.current) observer.observe(statsRef.current);
    return () => observer.disconnect();
  }, []);

  const handleShowModal = (plan: string) => { setSelectedPlan(plan); setShowModal(true); };
  const handleCloseModal = () => {
    setShowModal(false);
    setFormData({ nome: '', email: '', telefone: '', empresa: '', mensagem: '' });
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setShowSuccess(true);
    setTimeout(() => { setShowSuccess(false); handleCloseModal(); }, 3000);
  };

  return (
    <div className="page-container ddc-page">
      <PageMeta
        title="8º Dia de Campo Agricampanha 2026"
        description="O maior evento agropecuário da metade sul do RS. 8ª edição em 05 de março de 2026 na Agropecuária San Diego, Bagé/RS. Patrocínios, expositores e informações."
        url="/dia-de-campo"
      />

      {/* ── Header padrão com logo ── */}
      <div className="page-header ddc-header">
        <div className="page-header-bg" style={{ backgroundImage: "url('/img/hero-image.jpg')" }} />
        <div className="page-header-overlay" />
        <div className="ddc-header-logo">
          <img src="/img/dia-de-campo/logo-dia-de-campo.png" alt="8º Dia de Campo Agricampanha" className="ddc-logo" />
        </div>
        <div className="page-header-fita" style={{ backgroundImage: "url('/img/bg-fita.png')" }} />
      </div>

      {/* ── Data + Local ── */}
      <div className="ddc-event-bar">
        <Container>
          <div className="ddc-event-bar-inner">
            <div className="ddc-event-item">
              <i className="bi bi-calendar-event"></i>
              <div>
                <span className="ddc-event-label">Data do evento</span>
                <span className="ddc-event-value">05 de março de 2026</span>
              </div>
            </div>
            <div className="ddc-event-divider" />
            <div className="ddc-event-item">
              <i className="bi bi-geo-alt-fill"></i>
              <div>
                <span className="ddc-event-label">Local</span>
                <span className="ddc-event-value">Agropecuária San Diego — Bagé/RS</span>
              </div>
            </div>
          </div>
        </Container>
      </div>

      {/* ── Contadores ── */}
      <div className="ddc-stats-band" ref={statsRef}>
        {stats.map(s => (
          <StatItem key={s.label} value={s.value} suffix={s.suffix} label={s.label} active={statsActive} />
        ))}
      </div>

      {/* ── Galeria / Slider ── */}
      <div className="ddc-slider-band">
        <div className="ddc-slider">
          {slides.map((slide, i) => (
            <img key={i} src={slide.src} alt={slide.alt} className={`ddc-slide${i === current ? ' active' : ''}`} />
          ))}
          {slides.length > 1 && (
            <>
              <button className="ddc-arrow ddc-arrow--prev" onClick={() => goTo((current - 1 + slides.length) % slides.length, slides.length)} aria-label="Anterior">
                <i className="bi bi-chevron-left"></i>
              </button>
              <button className="ddc-arrow ddc-arrow--next" onClick={() => goTo((current + 1) % slides.length, slides.length)} aria-label="Próximo">
                <i className="bi bi-chevron-right"></i>
              </button>
            </>
          )}
          <div className="ddc-dots">
            {slides.map((_, i) => (
              <button key={i} className={`ddc-dot${i === current ? ' active' : ''}`} onClick={() => goTo(i, slides.length)} aria-label={`Foto ${i + 1}`} />
            ))}
          </div>
        </div>
      </div>

      {/* ── Conteúdo principal ── */}
      <Container className="ddc-content">

        <section className="ddc-section">
          <h2>Sobre o Dia de Campo</h2>
          <p>O Dia de Campo Agricampanha é considerado o maior evento do gênero na metade sul do RS. Realizado em parceria com diversas empresas do setor, conta com ampla cobertura da mídia local, estadual e nacional.</p>
          <p>Com o passar dos anos, o evento se consolidou como referência no setor agropecuário, registrando um crescimento de público de cerca de 50% a cada edição.</p>
        </section>

        <section className="ddc-section">
          <h2>Preparação e Objetivos</h2>
          <p>O Dia de Campo é resultado de meses de preparação, previsto para iniciar em 30 de outubro de 2025, com o plantio das parcelas experimentais e o tratamento contínuo das áreas, conduzidos em parceria com empresas apoiadoras.</p>
          <p>O objetivo principal é oferecer aos produtores estratégias para aumentar a produtividade e a rentabilidade por hectare de forma sustentável, maximizando a produção em áreas de integração entre agricultura e pecuária.</p>
        </section>

        <section className="ddc-section">
          <h2>E no ano de 2026...</h2>
          <p>O 8º Dia de Campo Agricampanha, com o tema "Semeando Conhecimento, Colhendo Resultado", acontecerá em fevereiro de 2026, na Agropecuária San Diego, em Bagé (RS). O evento reunirá cerca de 2 mil participantes e contará com uma programação diversificada, incluindo:</p>
          <ul className="ddc-list">
            <li>Palestras com especialistas renomados do setor agropecuário.</li>
            <li>Apresentação de novas tecnologias e soluções para o campo.</li>
            <li>Workshops e debates sobre temas fundamentais para o agronegócio.</li>
            <li>Mais de 50 empresas expositoras com estandes apresentando seus produtos e inovações.</li>
          </ul>
        </section>

        <section className="ddc-section">
          <h2>Números do evento</h2>
          <div className="text-center">
            <img src="/img/dia-de-campo/cronologia.png" alt="Cronologia do evento" className="img-fluid" />
          </div>
        </section>

        <section className="ddc-section">
          <h2>Resultados de 2025</h2>
          <div className="text-center">
            <img src="/img/dia-de-campo/dia-de-campo-2025.png" alt="Resultados 2025" className="img-fluid" />
          </div>
        </section>

        {/* ── Quero Investir ── */}
        <section className="ddc-section ddc-investir">
          <h2>Quero Investir</h2>
          <p className="ddc-investir-sub">Seja parceiro do maior evento agropecuário da metade sul do RS e conecte sua marca com milhares de produtores.</p>

          {/* Patrocínios */}
          <Row className="g-4 mb-5">
            {patrocinios.map(p => (
              <Col key={p.key} md={4}>
                <div className={`ddc-plan-card${p.destaque ? ' ddc-plan-card--destaque' : ''}`}>
                  {p.destaque && <div className="ddc-plan-badge">Mais completo</div>}
                  <div className="ddc-plan-header">
                    <h3 className="ddc-plan-nome">{p.nome}</h3>
                    <div className="ddc-plan-preco">{p.preco}</div>
                  </div>
                  <ul className="ddc-plan-list">
                    {p.beneficios.map((b, i) => <li key={i}>{b}</li>)}
                  </ul>
                  <button className="ddc-plan-btn" onClick={() => handleShowModal(p.nome)}>
                    Quero investir <i className="bi bi-arrow-right ms-1"></i>
                  </button>
                </div>
              </Col>
            ))}
          </Row>

          {/* Expositores */}
          <div className="ddc-expositores-title">
            <h3>Cota de Expositores</h3>
          </div>
          <Row className="g-4">
            {expositores.map(e => (
              <Col key={e.key} md={4}>
                <div className="ddc-plan-card ddc-plan-card--expositor">
                  <div className="ddc-plan-header">
                    <h3 className="ddc-plan-nome">{e.nome}</h3>
                    <div className="ddc-plan-preco">{e.preco}</div>
                  </div>
                  <ul className="ddc-plan-list">
                    {e.beneficios.map((b, i) => <li key={i}>{b}</li>)}
                  </ul>
                  <button className="ddc-plan-btn" onClick={() => handleShowModal(e.nome)}>
                    Quero investir <i className="bi bi-arrow-right ms-1"></i>
                  </button>
                </div>
              </Col>
            ))}
          </Row>
        </section>

      </Container>

      {/* ── Modal ── */}
      <Modal show={showModal} onHide={handleCloseModal} size="lg">
        <Modal.Header closeButton style={{ backgroundColor: 'var(--color-primary-dark)', color: 'white' }}>
          <Modal.Title style={{ fontFamily: 'var(--font-heading)' }}>Quero Investir — {selectedPlan}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {showSuccess && <Alert variant="success">Solicitação enviada! Em breve entraremos em contato.</Alert>}
          <Form onSubmit={handleSubmit}>
            <Row className="g-3">
              <Col md={6}><Form.Group><Form.Label>Nome *</Form.Label><Form.Control required value={formData.nome} onChange={e => setFormData({...formData, nome: e.target.value})} /></Form.Group></Col>
              <Col md={6}><Form.Group><Form.Label>Email *</Form.Label><Form.Control type="email" required value={formData.email} onChange={e => setFormData({...formData, email: e.target.value})} /></Form.Group></Col>
              <Col md={6}><Form.Group><Form.Label>Telefone *</Form.Label><Form.Control type="tel" required value={formData.telefone} onChange={e => setFormData({...formData, telefone: e.target.value})} /></Form.Group></Col>
              <Col md={6}><Form.Group><Form.Label>Empresa *</Form.Label><Form.Control required value={formData.empresa} onChange={e => setFormData({...formData, empresa: e.target.value})} /></Form.Group></Col>
              <Col xs={12}><Form.Group><Form.Label>Mensagem</Form.Label><Form.Control as="textarea" rows={3} value={formData.mensagem} onChange={e => setFormData({...formData, mensagem: e.target.value})} /></Form.Group></Col>
            </Row>
            <div className="text-end mt-3">
              <Button variant="secondary" onClick={handleCloseModal} className="me-2">Cancelar</Button>
              <Button variant="primary" type="submit">Enviar Solicitação</Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>

    </div>
  );
};

export default DiaDeCampo;
